import React, { lazy, Suspense, useEffect, useState } from "react";
import { Button } from "@heroui/button";
import { Tab, Tabs } from "@heroui/tabs";
import { useDisclosure } from "@heroui/modal";
import {
  ChevronsRight,
  Dna,
  Fence,
  PawPrint,
  Tractor,
  User,
  UserRoundPlus,
} from "lucide-react";
import { ErrorBoundary } from "react-error-boundary";

import "driver.js/dist/driver.css";
import { Farmer } from "../features/Farmers/FarmerSVG.tsx";

import { ErrorFallback, SuspenseFallback } from "@/lib/Utils.Components.tsx";
// import DataVisualisation from "@/features/Admin/Data-visualisation.tsx";
import {
  fetchAllAnimals,
  fetchAllFarmers,
  fetchAllFarms,
  fetchAllPhenotype,
  fetchAllSupervisors,
  useGetPhenotype,
} from "@/features/Admin/Admin.api.ts";
import { GuestRegistration } from "@/features/Form/GuestForm.tsx";
import SilaAnimalsTable from "@/features/Animals/SilaAnimalsTable.tsx";
import { useQueryClient } from "@tanstack/react-query";
import { adminKeys } from "@/config/Query.Keys.ts";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from "@heroui/drawer";
import { Tooltip } from "@heroui/tooltip";

const SupervisorRegistry = lazy(
  () => import("../features/Form/SupervisorRegistryForm"),
);

const DataVisualisation = lazy(
  () => import("@/features/Admin/Data-visualisation.tsx"),
);
const AnimalsTable = lazy(() => import("../features/Admin/AnimalsTable"));
const FarmersTable = lazy(() => import("../features/Farmers/Farmers.Table"));
const NewFarmersTable = lazy(
  () => import("../features/Farmers/Farmers.New.Table"),
);
const FarmsTable = lazy(() => import("../features/Farms/Farms.Table"));
const OfficialsTable = lazy(() => import("../features/Admin/OfficialsTable"));
const SupervisorTable = lazy(
  () => import("../features/Supervisor/Supervisor.Table"),
);

// FIX 1: Create a specific type for your tab keys. This improves type safety.
type AdminTabKey =
  | "Animals"
  | "Farms"
  | "Supervisors"
  | "Farmers"
  | "New Farmers"
  | "Government Officials"
  | "Sila Owned Animals";

function Admin() {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  // const [searchParams] = useSearchParams();
  // const type = searchParams.get("type");

  const queryClient = useQueryClient();
  const { data } = useGetPhenotype();
  const [selected, setSelected] = useState<AdminTabKey>("Animals");

  useEffect(() => {
    // Fire-and-forget prefetch calls on component mount.
    // No need to await, as we just want to initiate the fetches in the background.
    queryClient
      .prefetchQuery({
        queryKey: adminKeys.allAnimals(),
        queryFn: fetchAllAnimals,
      })
      .then(() => {});
    queryClient.prefetchQuery({
      queryKey: adminKeys.allFarmers(),
      queryFn: fetchAllFarmers,
    });
    queryClient.prefetchQuery({
      queryKey: adminKeys.allFarms(),
      queryFn: fetchAllFarms,
    });
    queryClient.prefetchQuery({
      queryKey: adminKeys.allSupervisors(),
      queryFn: fetchAllSupervisors,
    });
    queryClient.prefetchQuery({
      queryKey: adminKeys.phenotype(),
      queryFn: fetchAllPhenotype,
    });
    // Note: Ensure `allOfficials` has a queryFn if it needs to fetch data.
    queryClient.prefetchQuery({ queryKey: adminKeys.allOfficials() });
  }, [queryClient]);

  return (
    <>
      <div
        className={
          "w-full h-full flex flex-col overflow-x-hidden py-2 z-10 dark:bg-stone-950"
        }
      >
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Tabs
            key={"tabs"}
            size={"sm"}
            aria-label={"Tabs"}
            className={"max-w-full"}
            classNames={{
              base: " z-50",
              tabList:
                " mx-1 border-gray-2 z-50 " +
                " overflow-x-auto dark:border-2" +
                " dark:border-stone-800",
              tabContent:
                "text-[#015354ff] z-50" +
                " group-data-[selected=true]:text-white ",
              cursor: "bg-[#008c8fff]",
            }}
            selectedKey={selected}
            onSelectionChange={(key) => setSelected(key as AdminTabKey)}
            variant={"solid"}
          >
            <Tab
              key={"Animals"}
              // href={"/admin?type=Animals"}
              id={"Animals"}
              title={
                <div className="flex items-center space-x-2">
                  <PawPrint size={"22px"} />
                  <span>Animals</span>
                </div>
              }
            > 
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {selected === "Animals" && (
                  <Suspense fallback={<SuspenseFallback />}>
                    <AnimalsTable />
                  </Suspense>
                )}
              </ErrorBoundary>
            </Tab>
            <Tab
              key={"Farms"}
              // href={"/admin?type=Farms"}
              id={"Farms"}
              title={
                <div className="flex items-center space-x-2">
                  <Tractor size={"22px"} />
                  <span>Farms</span>
                </div>
              }
            >
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {selected === "Farms" && (
                  <Suspense fallback={<SuspenseFallback />}>
                    <FarmsTable />
                  </Suspense>
                )}
              </ErrorBoundary>
            </Tab>
            <Tab
              key={"Supervisors"}
              className={"flex flex-col gap-1 w-full"}
              // href={`/admin?type=Supervisors`}
              id={"Supervisors"}
              title={
                <div className="flex items-center space-x-2">
                  <User size={"22px"} />
                  <span>Supervisors</span>
                </div>
              }
            >
              {selected === "Supervisors" && (
                <Button
                  className={
                    "bg-[#015354ff] lg:w-44 lg:absolute lg:right-20" +
                    " sm:relative" +
                    " sm:w-full text-white"
                  }
                  size={"sm"}
                  variant={"solid"}
                  onPress={onOpen}
                >
                  Register Supervisor
                </Button>
              )}

              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {selected === "Supervisors" && (
                  <Suspense fallback={<SuspenseFallback />}>
                    <SupervisorTable />
                  </Suspense>
                )}
              </ErrorBoundary>
            </Tab>
            <Tab
              key={"Farmers"}
              // href={`/admin?type=Farmers`}
              id={`Farmers`}
              title={
                <div className="flex items-center space-x-2">
                  <Farmer />
                  <span>Farmers</span>
                </div>
              }
            >
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {selected === "Farmers" && (
                  <Suspense fallback={<SuspenseFallback />}>
                    <FarmersTable />
                  </Suspense>
                )}
              </ErrorBoundary>
            </Tab>
            <Tab
              key={"New Farmers"}
              // href={`/admin?type=New Farmers`}
              id={"New Farmers"}
              title={
                <div className="flex flex-row gap-2 justify-center items-center space-x-2">
                  <UserRoundPlus />
                  <span>New Farmers</span>
                  {/* <Chip color={"warning"}> new Farmers</Chip> */}
                </div>
              }
            >
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {selected === "New Farmers" && (
                  <Suspense fallback={<SuspenseFallback />}>
                    <NewFarmersTable />
                  </Suspense>
                )}
              </ErrorBoundary>
            </Tab>
            <Tab
              key={"Government Officials"}
              className={"flex flex-col gap-1 w-full"}
              // href={`/admin?type=Government Officials`}
              id={`Government Officials`}
              title={
                <div className="flex items-center space-x-2">
                  <Dna />
                  <span>Government Officials</span>
                </div>
              }
            >
              {/* <div className={"w-full overflow-x-auto"}> */}
              {selected === "Government Officials" && (
                <div className={"w-full justify-end"}>
                  <Button
                    className={
                      "bg-[#015354ff] lg:w-44 lg:absolute lg:right-20" +
                      " sm:relative" +
                      " sm:w-full text-white"
                    }
                    size={"sm"}
                    variant={"solid"}
                    onPress={onOpen}
                  >
                    Register Government Official
                  </Button>
                </div>
              )}
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {selected === "Government Officials" && (
                  <Suspense fallback={<SuspenseFallback />}>
                    <OfficialsTable />
                  </Suspense>
                )}
              </ErrorBoundary>
            </Tab>

            <Tab
              key={"Sila Owned Animals"}
              id={"Sila Owned Animals"}
              title={
                <div className="flex items-center space-x-2">
                  <Fence size={"22px"} />
                  <span>Sila Owned Animals</span>
                </div>
              }
            >
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                {selected === "Sila Owned Animals" && (
                  <Suspense fallback={<SuspenseFallback />}>
                    <SilaAnimalsTable />
                  </Suspense>
                )}
              </ErrorBoundary>
            </Tab>
          </Tabs>

          <div className={"w-full overflow-x-auto z-0"}>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Suspense fallback={<SuspenseFallback />}>
                <DataVisualisation data={data} id={"DataVisualizer"} />
              </Suspense>
            </ErrorBoundary>
          </div>
        </ErrorBoundary>
      </div>
      <Drawer isOpen={isOpen} size={"xl"} onOpenChange={onOpenChange}>
        <DrawerContent>
          {(onClose) => (
            <>
              <DrawerHeader className="absolute top-0 inset-x-0 z-50 flex flex-row gap-2 px-2 py-2 border-b border-default-200/50 justify-between bg-content1/50 backdrop-saturate-150 backdrop-blur-lg">
                <Tooltip content="Close">
                  <Button
                    isIconOnly
                    className="text-default-800"
                    size="sm"
                    variant="light"
                    startContent={<ChevronsRight />}
                    onPress={onClose}
                  />
                </Tooltip>
              </DrawerHeader>
              <DrawerBody className="flex flex-col gap-4 p-4">
                <h2 className="text-lg font-semibold text-default-800">
                  {selected} Registration
                </h2>
                {selected === "Supervisors" && (
                  <Suspense fallback={<SuspenseFallback />}>
                    <SupervisorRegistry />
                  </Suspense>
                )}
                {selected === "Government Officials" && (
                  <Suspense fallback={<SuspenseFallback />}>
                    <GuestRegistration />
                  </Suspense>
                )}
              </DrawerBody>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}

const MemoizedAdmin = React.memo(Admin);

export default MemoizedAdmin;
